import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Form, InlineNotification, Loading, Tag, TextInput } from '@carbon/react';
import EmptyState from 'src/components/Base/EmptyState';
import Row from 'src/components/Base/Row';
import Spacing from 'src/components/Base/Spacing';
import EmployeeDashboardDayOff from 'src/components/Employee/EmployeeDashboardDayOff';
import EmployeeDashboardIssues from 'src/components/Employee/EmployeeDashboardIssues';
import MyEmploymentSummary from 'src/components/Employee/MyEmploymentSummary';
import TimesheetCard from 'src/components/Timesheet/TimesheetCard';
import { useTenantContext } from 'src/context/useTenantContext';
import { useEmployeeMonthlyDayOff } from 'src/hooks/useEmployeeDayOff';
import { useEmployeeMonthlyIssues } from 'src/hooks/useIssues';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useSelfEmployee } from 'src/hooks/useSelfEmployee';
import { tenantEmployeeUpdateMyProfile } from 'src/services/apis/tenantRequests';
import { EmployeeEntity } from 'src/services/apis/types';
import { EmployeeRoles } from 'src/utils/employee';
import { QUERIES } from 'src/utils/react-query';

/**
 * Same fallback chain as `toEmployeeRow` in src/features/employees.tsx: the linked Users row
 * wins once an employment is linked, the employment row's own columns cover account-less
 * hires. A `/me` caller has authenticated, so `user` is populated — but there is one
 * convention for this in the codebase and this is it.
 */
function displayName(employee: EmployeeEntity): string {
  const first = employee.user?.firstName ?? employee.firstName;
  const last = employee.user?.lastName ?? employee.lastName;
  return [first, last].filter(Boolean).join(' ');
}

function ProfileCard(props: { employee: EmployeeEntity }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId } = useTenantContext();
  const currentPhone = props.employee.user?.phoneNumber ?? props.employee.phoneNumber ?? '';
  const [phoneNumber, setPhoneNumber] = useState(currentPhone);

  // Seeded during render rather than in an effect, matching Profile.tsx: an effect would
  // commit the empty field first and replace it on the next pass.
  const [seeded, setSeeded] = useState(currentPhone);
  if (currentPhone !== seeded) {
    setSeeded(currentPhone);
    setPhoneNumber(currentPhone);
  }

  const save = useMutation(() => tenantEmployeeUpdateMyProfile({ orgSlug, restaurantId }, { phoneNumber }), {
    onSuccess: () => queryClient.invalidateQueries([QUERIES.EMPLOYEE_SELF, orgSlug, restaurantId]),
  });

  return (
    <div className="my-employment__profile">
      <Spacing space={2} />
      <h5 className="section__heading">{t('employee.myEmployment.profile')}</h5>
      <Form
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <TextInput
          id="my-employment-phone"
          labelText={t('common.phoneNumber')}
          // EmployeeSelfProfileDto caps this at 20 and deliberately allows '' so staff can
          // clear the field; only the length needs guarding client-side.
          maxLength={20}
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
        />
        <Spacing space={1} />
        <Button type="submit" disabled={save.isLoading || phoneNumber === currentPhone}>
          {t('actions.save')}
        </Button>
        {save.isSuccess && (
          <InlineNotification kind="success" title={t('employee.myEmployment.saved')} lowContrast hideCloseButton />
        )}
        {save.isError && (
          <InlineNotification kind="error" title={t('employee.myEmployment.saveFailed')} lowContrast hideCloseButton />
        )}
      </Form>
      {/* Spec decision 6: names, emails and credentials are owned by the auth service and
          flow inbound via the provisioning webhook, so the portal cannot edit them here. */}
      <p className="my-employment__account-note">
        {t('employee.myEmployment.accountManagedElsewhere')} <RouterLink to="/profile">{t('profile.title')}</RouterLink>
      </p>
    </div>
  );
}

/**
 * The staff-facing counterpart to EmployeeDashboard: same panels, but scoped to the signed-in
 * user by the server rather than by an owner's employee picker. EmployeeDashboard is untouched.
 */
const MyEmployment = () => {
  const { t } = useTranslation();
  const { employee, incomes, isLoading, isNotAnEmployee, isError, retry } = useSelfEmployee();
  const { data: branches } = useBranches();
  // Both panels take an employee id, but on this screen it is only ever the caller's own —
  // and after Task 13b the server self-scopes members regardless of what is asked for, which
  // is what actually keeps a colleague's fines out of this page. The id here is a filter.
  const {
    query: { data: monthlyIssues },
  } = useEmployeeMonthlyIssues(employee?.id ?? null);
  const {
    query: { data: monthlyDayOff },
  } = useEmployeeMonthlyDayOff(employee?.id ?? null);

  if (isNotAnEmployee) {
    return (
      <div className="my-employment">
        <EmptyState
          title={t('employee.myEmployment.notAnEmployeeTitle')}
          desc={t('employee.myEmployment.notAnEmployeeDescription')}
        />
      </div>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  // Anything that is not the NOT_AN_EMPLOYEE answer above lands here: a 500, a dropped
  // connection, an INSUFFICIENT_ORG_ROLE refusal, or a tenant context that never resolved a
  // restaurantId (which leaves the query disabled, so it is neither loading nor errored).
  // Rendering <Loading /> for those spun a full-screen overlay that never resolved.
  if (isError || !employee) {
    return (
      <div className="my-employment my-employment--error" data-testid="my-employment-error">
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t('employee.myEmployment.loadFailed')}
          subtitle={t('employee.myEmployment.loadFailedDescription')}
        />
        <Button kind="tertiary" onClick={retry} data-testid="my-employment-retry">
          {t('actions.retry')}
        </Button>
      </div>
    );
  }

  const branchName = branches?.data?.find((branch) => branch.id === employee.restaurantBranchId)?.name;
  const roleLabel = EmployeeRoles.find((item) => item.value === employee.role)?.label;

  return (
    <div className="employee-dashboard my-employment">
      <div className="employee-dashboard__main-content">
        <h1 className="employee-dashboard__heading">{displayName(employee)}</h1>
        {/* Row types its children as ReactElement[], so the conditional tags are collected
            into an array rather than inlined as `cond && <Tag/>` expressions. */}
        <Row gap={0.5} alignCenter>
          {[
            branchName ? (
              <Tag key="branch" size="md">
                {branchName}
              </Tag>
            ) : null,
            roleLabel ? (
              <Tag key="role" size="md" type={employee.role === 'E' ? 'green' : 'red'}>
                {roleLabel}
              </Tag>
            ) : null,
            employee.activated === false ? (
              <Tag key="inactive" size="md" type="gray">
                {t('employee.myEmployment.inactive')}
              </Tag>
            ) : null,
          ].filter((tag): tag is React.ReactElement => tag !== null)}
        </Row>
        {/* Hourly staff only (spec decision 13): pay needs an hourly rate. */}
        {employee.salaryUnit === 'H' && <TimesheetCard />}
        <MyEmploymentSummary employee={employee} incomes={incomes} />
        <EmployeeDashboardIssues employeeMonthlyIssues={monthlyIssues?.data} />
        <EmployeeDashboardDayOff employeeMonthlyDayOff={monthlyDayOff?.data} />
        <ProfileCard employee={employee} />
      </div>
    </div>
  );
};

export default MyEmployment;
